import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { type ModalShiftData } from "@src/appV2/redesign/Shift/types";
import { addDays, isAfter } from "date-fns";
import { useCallback, useMemo } from "react";

import {
  CHILDCARE_REFERRAL_POST_BOOKING_IAM_SHOWN_AT_STORAGE_KEY,
  DEFAULT_CHILDCARE_REFERRAL_POST_BOOKING_IAM_CONFIG,
} from "./constants";

interface UseChildcareReferralPostBookingIamResult {
  isEligible: boolean;
  markAsShown: () => void;
  targetingContext: {
    workplaceType: string | undefined;
    msa: string | undefined;
  };
}

export function useChildcareReferralPostBookingIam(
  shift: ModalShiftData
): UseChildcareReferralPostBookingIamResult {
  const config = useCbhFlag(CbhFeatureFlag.CHILDCARE_REFERRAL_POST_BOOKING_IAM, {
    defaultValue: DEFAULT_CHILDCARE_REFERRAL_POST_BOOKING_IAM_CONFIG,
  });

  const [lastShownAtIso, setLastShownAtIso] = useLocalStorage<string | undefined>(
    CHILDCARE_REFERRAL_POST_BOOKING_IAM_SHOWN_AT_STORAGE_KEY,
    undefined
  );

  const { type: workplaceType } = shift.workplace.attributes;
  const { msa } = shift.workplace.attributes.location;

  const isEligible = useMemo(() => {
    if (!config.enabled) {
      return false;
    }

    const matchesWorkplaceType =
      Boolean(workplaceType) && config.workplaceTypes.includes(workplaceType ?? "");
    const matchesMsa = Boolean(msa) && config.msas.includes(msa ?? "");

    if (!matchesWorkplaceType || !matchesMsa) {
      return false;
    }

    if (lastShownAtIso) {
      const nextEligibleAt = addDays(new Date(lastShownAtIso), config.frequencyCapInDays);
      if (isAfter(nextEligibleAt, new Date())) {
        return false;
      }
    }

    return true;
  }, [config, workplaceType, msa, lastShownAtIso]);

  const markAsShown = useCallback(() => {
    setLastShownAtIso(new Date().toISOString());
  }, [setLastShownAtIso]);

  return {
    isEligible,
    markAsShown,
    targetingContext: {
      workplaceType,
      msa,
    },
  };
}
